import { useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation } from "convex/react"
import { api } from "../../convex/_generated/api"
import { navigateSafeReturn } from "@/lib/auth-client"
import { useOnboardingGate } from "@/hooks/use-onboarding-gate"
import { usePendingAction } from "@/hooks/use-pending-action"
import { usePrefillOnce } from "@/hooks/use-prefill-once"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { FormInput } from "@/components/form/form-field"
import { Skeleton } from "@/components/ui/skeleton"

export const Route = createFileRoute("/onboarding/company")({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
  component: CompanyOnboarding,
})

function CompanyOnboarding() {
  const { returnTo: returnToSearch } = Route.useSearch()
  const navigate = useNavigate()
  const gate = useOnboardingGate({
    role: "employer",
    returnTo: returnToSearch,
    onEnsured: ({ claimedCompany }) => {
      if (claimedCompany) {
        // Joined an existing company via a team invite — no company to
        // create here, go straight to the recruit desk.
        navigateSafeReturn(navigate, returnToSearch, "/recruit")
      }
    },
  })
  const profile = gate.status === "ready" ? gate.profile : undefined
  const createCompany = useMutation(api.companies.createForCurrentUser)

  const [companyName, setCompanyName] = useState("")
  const [website, setWebsite] = useState("")
  const [linkedin, setLinkedin] = useState("")
  const { pending: saving, run: runSave } = usePendingAction()

  usePrefillOnce(profile, (p) => {
    setCompanyName(p.companyName ?? "")
    setWebsite(p.companyWebsite ?? "")
    setLinkedin(p.companyLinkedin ?? "")
  })

  if (gate.status !== "ready" || gate.profile.role === "seeker") {
    return (
      <div className="mkt-page home-wf">
        <div className="mkt-page-shell mkt-page-shell--narrow">
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    )
  }

  const submitCompany = async () => {
    const name = companyName.trim()
    const site = website.trim()
    const linkedinUrl = linkedin.trim()
    if (!name || !site || !linkedinUrl) return
    if (
      await runSave(async () => {
        await createCompany({ name, website: site, linkedin: linkedinUrl })
      }, {
        error: "Failed to save",
      })
    ) {
      // A recruiter who did not arrive from a protected deep link finishes in
      // their desk. Preserve a valid `returnTo` (for example, a job draft)
      // so the original task remains uninterrupted.
      navigateSafeReturn(navigate, returnToSearch, "/recruit")
    }
  }

  return (
    <div className="mkt-page home-wf">
      <div className="mkt-page-shell mkt-page-shell--narrow flex min-h-[calc(100svh-3.5rem)] items-center py-10">
        <Card className="mkt-auth-card w-full border-[rgb(12_15_20/0.08)] shadow-[0_18px_40px_rgb(12_15_20/0.06)]">
          <CardHeader>
            <CardTitle className="font-extrabold tracking-tight">
              Set up your company
            </CardTitle>
            <CardDescription>
              Website and LinkedIn are required so we can verify you. About and
              other socials can wait until Settings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault()
                void submitCompany()
              }}
            >
              <FormInput
                id="co-name"
                label="Company name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Labs"
                autoFocus
                required
              />
              <FormInput
                id="co-website"
                label="Website"
                description="Must match your work-email domain before you can post jobs."
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://acme.dev"
                required
              />
              <FormInput
                id="co-linkedin"
                label="Company LinkedIn"
                description="Reviewers use this to confirm the company is real."
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                placeholder="linkedin.com/company/acme"
                required
              />
              <Button
                type="submit"
                className="w-full rounded-full bg-[color:var(--wf-lime)] font-bold text-[color:var(--wf-ink)] hover:brightness-105"
                disabled={
                  saving ||
                  !companyName.trim() ||
                  !website.trim() ||
                  !linkedin.trim()
                }
              >
                {saving ? "Saving…" : "Continue"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
